import { NewsArticleUpdateContentRequest, NewsArticleUpdateContentResponse } from '@models/news-article';
import { NewsArticleGroup } from '@models/news-article-group';
import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { NewsArticlesService } from '../news-articles.service';
import { addDataToListItem, findAndReplaceByIdWithSort, NewsArticlesStore } from '../news-articles.store';

export const getNewsArticleConfigurationHandlingMethods = (injects: {
  store: NewsArticlesStore;
  newsArticlesService: NewsArticlesService;
}) => {
  const { store, newsArticlesService } = injects;
  const activateNewsArticle = rxMethod<string>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true, isNewsArticlesByCommunityLoading: true })),
      switchMap(id =>
        newsArticlesService.activate(id).pipe(
          tap(newsArticle => {
            patchState(store, {
              isSelectedNewsArticleLoading: false,
              selectedNewsArticle: { content: store.selectedNewsArticle()?.content ?? null, ...newsArticle },
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), newsArticle),
              isLoading: false,
              isNewsArticlesByCommunityLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false, isNewsArticlesByCommunityLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const deactivateNewsArticle = rxMethod<string>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true, isNewsArticlesByCommunityLoading: true })),
      switchMap(id =>
        newsArticlesService.deactivate(id).pipe(
          tap(newsArticle => {
            patchState(store, {
              isSelectedNewsArticleLoading: false,
              selectedNewsArticle: addDataToListItem(newsArticle, store.selectedNewsArticle()),
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), newsArticle),
              isLoading: false,
              isNewsArticlesByCommunityLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false, isNewsArticlesByCommunityLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const setNewsArticleContent = rxMethod<{ id: string; content: NewsArticleUpdateContentRequest }>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true })),
      switchMap(({ id, content }) =>
        newsArticlesService.setContent(id, content).pipe(
          tap((newContent: NewsArticleUpdateContentResponse) => {
            patchState(store, {
              selectedNewsArticle: { ...store.selectedNewsArticle()!, content: newContent.content },
              isSelectedNewsArticleLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const updateWorkloadsForGroupArticles = (group: NewsArticleGroup) => {
    patchState(store, {
      selectedNewsArticle:
        store.selectedNewsArticle() && store.selectedNewsArticle()!.groupId === group.id
          ? { ...store.selectedNewsArticle()!, workloads: group.workloads }
          : store.selectedNewsArticle(),
      newsArticles: store
        .newsArticles()
        .map(article => (article.groupId === group.id ? { ...article, workloads: group.workloads } : article)),
      newsArticlesByCommunity: store
        .newsArticlesByCommunity()
        .map(article => (article.groupId === group.id ? { ...article, workloads: group.workloads } : article)),
    });
  };

  const updateMembershipLevelsForGroupArticles = (group: NewsArticleGroup) => {
    patchState(store, {
      selectedNewsArticle:
        store.selectedNewsArticle() && store.selectedNewsArticle()!.groupId === group.id
          ? { ...store.selectedNewsArticle()!, membershipLevels: group.membershipLevels }
          : store.selectedNewsArticle(),
      newsArticles: store
        .newsArticles()
        .map(article => (article.groupId === group.id ? { ...article, membershipLevels: group.membershipLevels } : article)),
      newsArticlesByCommunity: store
        .newsArticlesByCommunity()
        .map(article => (article.groupId === group.id ? { ...article, membershipLevels: group.membershipLevels } : article)),
    });
  };

  return {
    activateNewsArticle,
    deactivateNewsArticle,
    setNewsArticleContent,
    updateWorkloadsForGroupArticles,
    updateMembershipLevelsForGroupArticles,
  };
};

export interface NewsArticleConfigurationHandlingStoreType {
  readonly activateNewsArticle: (id: string) => void;
  readonly deactivateNewsArticle: (id: string) => void;
  readonly setNewsArticleContent: (params: { id: string; content: NewsArticleUpdateContentRequest }) => void;
  readonly updateWorkloadsForGroupArticles: (group: NewsArticleGroup) => void;
  readonly updateMembershipLevelsForGroupArticles: (group: NewsArticleGroup) => void;
}
